// src/App.js
import { Routes, Route, useLocation } from "react-router-dom";
import { Suspense, lazy } from "react";
import Header from "./components/Header/Header";
import MobileFloatingCartBar from "./components/common/MobileFloatingCartBar";
import Footer from "./components/Footer/Footer";
import Home from "./components/Home/Home";
import RegisterVendor from "./components/Register/RegisterVendor";
import VendorLogin from "./components/Login/VendorLogin";
import ProtectedRoute from "./components/VendorPortal/ProtectedRoute";
import AdminLogin from "./components/Admin/AdminLogin";
import AdminRoute from "./components/AdminRoute";
import ProductDetails from "./components/Home/ProductDetails";
import CheckoutPage from "./components/Home/CheckoutPage";
import SingleVendorRoot from "./components/Home/SingleVendorRoot";
import NotFound from "./components/NotFound";
import ScrollToTop from "./components/common/ScrollToTop";
import StorefrontDocumentTitle from "./components/common/StorefrontDocumentTitle";
import AnnouncementBar from "./components/common/AnnouncementBar";
import UserDashboard from "./components/User/UserDashboard";
import UserAuthForm from "./components/User/UserAuthForm";
import UserProtectedRoute from "./components/User/UserProtectedRoute";
import About from "./components/Home/About";
import Contact from "./components/Home/Contact";
import LegalPage from "./components/Home/LegalPage";
import VendorStorePage from "./components/Home/VendorStorePage";
import VendorStoreLayout from "./components/Home/VendorStoreLayout";
import { VendorStoreProvider } from "./context/VendorStoreContext";
import Loader from "./components/Loader/Loader";
import { useLoading } from "./context/LoadingContext";
import SuccessPage from "./components/User/SuccessPage";
import Wishlist from "./components/User/Wishlist";
import OrderDetailPage from "./components/User/OrderDetailPage";
import { useMaintenanceGate } from "./hooks/useMaintenanceGate";
import MaintenancePage from "./components/Maintenance/MaintenancePage";
import { FeatureFlagsContext } from "./context/FeatureFlagsContext";
import PostHogPageView from "./components/common/PostHogPageView";

const VendorPortal = lazy(() =>
  import("./components/VendorPortal/VendorPortal")
);
const AdminDashboard = lazy(() => import("./components/Admin/AdminDashboard"));

function App() {
  const { isLoading } = useLoading();
  const location = useLocation();
  const isAdminSection = location.pathname.startsWith("/admin");
  const maintenance = useMaintenanceGate();

  if (!maintenance.ready && !isAdminSection) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-950 font-sans text-slate-300">
        {isLoading && <Loader />}
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-rose-400 border-t-transparent" />
        <p className="mt-4 text-sm">Checking storefront status…</p>
      </div>
    );
  }

  if (maintenance.enabled && !isAdminSection) {
    return (
      <>
        {isLoading && <Loader />}
        <ScrollToTop />
        <MaintenancePage
          headline={maintenance.headline}
          bodyText={maintenance.bodyText}
          onRecheck={maintenance.refetch}
          rechecking={maintenance.rechecking}
        />
      </>
    );
  }

  return (
    <FeatureFlagsContext.Provider value={maintenance.featureFlags}>
      <VendorStoreProvider>
      <div className="flex flex-col min-h-screen font-sans bg-gray-50 text-gray-900">
        {isLoading && <Loader />}
        <ScrollToTop />
        <PostHogPageView />
        <StorefrontDocumentTitle />
        <Header />
        <AnnouncementBar />
        <MobileFloatingCartBar />
        <main className="flex-grow">
          <Suspense fallback={<div className="text-center p-10">Loading...</div>}>
            <Routes>
              <Route path="/" element={<SingleVendorRoot />} />
              <Route path="/shop" element={<Home />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/products/:productId" element={<ProductDetails />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<LegalPage slug="privacy-policy" />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/order-success" element={<SuccessPage />} />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/order/:id" element={<OrderDetailPage />} />
              <Route
                path="/user-login"
                element={<UserAuthForm isLogin={true} />}
              />
              <Route
                path="/user-signup"
                element={<UserAuthForm isLogin={false} />}
              />
              <Route
                path="/user/dashboard"
                element={
                  <UserProtectedRoute>
                    <UserDashboard />
                  </UserProtectedRoute>
                }
              />

              <Route path="/vendor/register" element={<RegisterVendor />} />

              <Route path="/vendor/login" element={<VendorLogin />} />

              <Route
                path="/vendor/dashboard/*"
                element={
                  <ProtectedRoute>
                    <VendorPortal />
                  </ProtectedRoute>
                }
              />

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin/dashboard/*"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              {/* Per-vendor white-label store — entire site becomes the vendor's brand */}
              <Route path="/:storeSlug" element={<VendorStoreLayout />}>
                <Route index element={<VendorStorePage />} />
                <Route path="about" element={<About />} />
                <Route path="contact" element={<Contact />} />
                <Route path="products/:productId" element={<ProductDetails />} />
                <Route path="checkout" element={<CheckoutPage />} />
                <Route path="order-success" element={<SuccessPage />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
      </VendorStoreProvider>
    </FeatureFlagsContext.Provider>
  );
}
export default App;
